import { Editor } from '@tiptap/react';
import cx from 'classnames';
import { ContentVariable } from 'components/learning-center/assignments/types';
import { ButtonV2, FormV2, ISelectOption } from 'components/ui';
import { Modal } from 'components/ui/Modal';
import _ from 'lodash';
import { ChangeEvent, useState } from 'react';
import { ModalProps } from 'react-bootstrap';
import { useCmsCourseVariables } from '../../hooks';
import css from './CourseVariablesModal.module.scss';

const HYPERLINK_REGEX_EXP = /linkTo/i;
const EMAIL_REGEX_EXP = /.*Email/i;

type VariableType = 'text' | 'hyperlink' | 'email' | 'assessment';

const VARIABLE_TYPE_OPTIONS: ISelectOption<VariableType>[] = [
  { label: 'Text', value: 'text' },
  { label: 'Hyperlink', value: 'hyperlink' },
  { label: 'Email', value: 'email' },
  { label: 'Assessment', value: 'assessment' },
];

interface ICourseVariablesModalProps extends ModalProps {
  courseId: number;
  editor: Editor;
  isVisible: boolean;
  onHide: () => void;
}

export const CourseVariablesModal = ({
  courseId,
  editor,
  isVisible,
  onHide,
  ...rest
}: ICourseVariablesModalProps) => {
  const [variableType, setVariableType] = useState<VariableType>('text');
  const [displayName, setDisplayName] = useState<string>('');
  const [variableName, setVariableName] = useState<string>('');

  const { data: courseVariables } = useCmsCourseVariables(courseId);

  const hyperlinkVariables = _.filter(courseVariables, (v) =>
    HYPERLINK_REGEX_EXP.test(v.variableName)
  );
  const emailVariables = _.filter(courseVariables, (v) =>
    EMAIL_REGEX_EXP.test(v.variableName)
  );
  const textVariables = _.filter(
    courseVariables,
    (v) =>
      !HYPERLINK_REGEX_EXP.test(v.variableName) &&
      !EMAIL_REGEX_EXP.test(v.variableName)
  );

  const handleHide = () => {
    onHide();
    setVariableType('text');
    setDisplayName('');
    setVariableName('');
  };

  const handleVariableTypeChange = (selected: ISelectOption<VariableType>) => {
    setVariableType(selected.value);
    setDisplayName('');
    setVariableName('');
  };

  const handleDisplayNameChange = (ev: ChangeEvent<HTMLInputElement>) => {
    setDisplayName(ev.target.value);
  };

  const handleVariableChange = (selected: ISelectOption<string>) => {
    if (variableType === 'text' || variableType === 'assessment') {
      setDisplayName(selected.label);
    }
    setVariableName(selected.value);
  };

  const getVariableOptions = () => {
    switch (variableType) {
      case 'hyperlink':
        return _.map(hyperlinkVariables, (v) => ({
          label: v.displayName,
          value: v.variableName,
        }));
      case 'email':
        return _.map(emailVariables, (v) => ({
          label: v.displayName,
          value: v.variableName,
        }));
      case 'text':
        return _.chain(textVariables)
          .map((v) => ({ label: v.displayName, value: v.variableName }))
          .concat([
            { label: 'Name', value: ContentVariable.LEARNER_NAME },
            { label: 'Company Name', value: ContentVariable.COMPANY_NAME },
            { label: 'State', value: ContentVariable.LEARNER_STATE },
          ])
          .value();
      case 'assessment':
        return [
          {
            label: 'Number of questions (#)',
            value: ContentVariable.ASSESSMENT_NUM_QUESTIONS,
          },
          {
            label: 'Assessment score (%)',
            value: ContentVariable.ASSESSMENT_SCORE,
          },
          {
            label: 'Time saved (# minutes)',
            value: ContentVariable.ASSESSMENT_TIME_SAVED,
          },
          {
            label: 'Questions answered correctly (#)',
            value: ContentVariable.ASSESSMENT_NUM_CORRECT,
          },
          {
            label: 'Modules skipped (#)',
            value: ContentVariable.ASSESSMENT_NUM_MODULES_SKIPPED,
          },
          {
            label: 'Modules remaining (#)',
            value: ContentVariable.ASSESSMENT_NUM_MODULES_REMAINING,
          },
          {
            label: 'Passed & failed messages',
            value: ContentVariable.ASSESSMENT_PASSED,
          },
        ];
      default:
        return [];
    }
  };

  const handleInsert = () => {
    switch (variableType) {
      case 'hyperlink':
        editor
          .chain()
          .focus()
          .insertContent(`{{#${variableName}}}`)
          .extendMarkRange('link')
          .setLink({ href: `{{${variableName}}}` })
          .insertContent(displayName)
          .unsetMark('link')
          .insertContent(
            `{{/${variableName}}}{{^${variableName}}}${displayName}{{/${variableName}}}`
          )
          .run();
        break;
      case 'email':
        editor
          .chain()
          .focus()
          .insertContent(`{{#${variableName}}}`)
          .extendMarkRange('link')
          .setLink({ href: `mailto:{{{${variableName}}}}` })
          .insertContent(displayName)
          .unsetMark('link')
          .insertContent(
            `{{/${variableName}}}{{^${variableName}}}${displayName}{{/${variableName}}}`
          )
          .run();
        break;
      case 'text':
        const isAlwaysDefined = _.includes(
          [ContentVariable.LEARNER_NAME, ContentVariable.COMPANY_NAME],
          variableName
        );
        if (isAlwaysDefined) {
          editor.chain().focus().insertContent(`{{${variableName}}}`).run();
        } else {
          editor
            .chain()
            .focus()
            .insertContent(`{{#${variableName}}}`)
            .insertContent(`{{${variableName}}}`)
            .insertContent(
              `{{/${variableName}}}{{^${variableName}}}${variableName}{{/${variableName}}}`
            )
            .run();
        }
        break;
      case 'assessment':
        editor
          .chain()
          .focus()
          .insertContent(`{{#${variableName}}}`)
          .insertContent(`{{${variableName}}}`)
          .insertContent(
            `{{/${variableName}}}{{^${variableName}}}${variableName}{{/${variableName}}}`
          )
          .run();
        break;
    }
    handleHide();
  };

  const isFormValid = () => {
    if (!variableName) return false;
    if (variableType === 'hyperlink' || variableType === 'email') {
      return !!displayName;
    }
    return true;
  };

  const shouldShowDisplayNameField =
    variableType === 'hyperlink' || variableType === 'email';
  const options = getVariableOptions();

  return (
    <Modal
      show={isVisible}
      className={cx(css.insertMustacheCodeModal)}
      hasCloseButton={false}
      backdropClassName={css.insertMustacheCodeModalBackdrop}
      {...rest}
    >
      <Modal.Header>
        <h2 className={css.heading}>Insert Variable</h2>
      </Modal.Header>
      <Modal.Body className={css.insertMustacheCodeModalBody}>
        <FormV2>
          <FormV2.Group>
            <FormV2.Label className={css.label}>Variable Type</FormV2.Label>
            <FormV2.Select
              onChange={handleVariableTypeChange}
              options={VARIABLE_TYPE_OPTIONS}
              value={_.find(
                VARIABLE_TYPE_OPTIONS,
                (option) => option.value === variableType
              )}
            />
          </FormV2.Group>

          {shouldShowDisplayNameField && (
            <FormV2.Group>
              <FormV2.Label className={css.label}>
                {variableType === 'hyperlink'
                  ? 'Link display name'
                  : 'Email display name'}
              </FormV2.Label>
              <FormV2.Control
                name="Display name"
                onChange={handleDisplayNameChange}
                placeholder="Name"
                value={displayName}
              />
            </FormV2.Group>
          )}

          <FormV2.Group>
            <FormV2.Label className={css.label}>
              {variableType === 'hyperlink' && 'Link variable'}
              {variableType === 'email' && 'Email variable'}
              {variableType === 'text' && 'Text variable'}
              {variableType === 'assessment' && 'Assessment variable'}
            </FormV2.Label>
            <FormV2.Select
              onChange={handleVariableChange}
              options={options}
              value={_.find(options, (option) => option.value === variableName)}
            />
          </FormV2.Group>
        </FormV2>
      </Modal.Body>
      <Modal.Footer>
        <div className={css.buttonContainer}>
          <ButtonV2 onClick={handleHide} variant="outline">
            Cancel
          </ButtonV2>
          <ButtonV2
            onClick={handleInsert}
            disabled={!isFormValid()}
            variant="solid"
            color="blue"
          >
            Insert
          </ButtonV2>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
